// Section 21 (stream G): the Ledger as a screen, also the win and loss screen: the outcome
// banner (with the seed to copy and the options), the tags as plain labels, the score line, the
// eleven sections as tabs, and the actions (continue in sandbox after a win, export the run,
// copy the summary, back). Props only; LedgerScreen (ledger-screen.tsx) wires the store.
import type { JobId } from '../../sim/api';
import { LEDGER_SECTIONS, type LedgerSectionId, type LedgerVM } from '../../viewModels/ledger';
import {
  ledgerOptionsLine,
  ledgerSectionLabel,
  ledgerText as t,
  tagLabel,
  tagMeaning,
} from '../text/en';
import {
  ChosenSection,
  FragilitySection,
  GateSection,
  InequalitySection,
  JusticeSection,
  KnowledgeSection,
  NormsSection,
  OutcomeSection,
  PeopleSection,
  SketchSection,
  WentWithoutSection,
} from './sections';
import { outcomeLine } from './summary';

export interface LedgerViewProps {
  vm: LedgerVM;
  jobLabel: (job: JobId) => string;
  section: LedgerSectionId;
  onSection: (section: LedgerSectionId) => void;
  /** Shown only after a win (vm.outcome.canContinueSandbox). */
  onContinueSandbox?: () => void;
  /** Stream F's export action, wired by the integration (not rendered when absent). */
  onExport?: () => void;
  onCopySeed: () => void;
  onCopySummary: () => void;
  /** What was just copied, for the "Copied." note. */
  copied: 'seed' | 'summary' | null;
  onBack: () => void;
}

function Section({ vm, section, jobLabel }: Pick<LedgerViewProps, 'vm' | 'section' | 'jobLabel'>) {
  const perYear = vm.seasonsPerYear;
  switch (section) {
    case 'outcome':
      return <OutcomeSection vm={vm} />;
    case 'people':
      return <PeopleSection vm={vm} />;
    case 'wentWithout':
      return <WentWithoutSection vm={vm} />;
    case 'chosen':
      return <ChosenSection vm={vm} />;
    case 'norms':
      return <NormsSection vm={vm} />;
    case 'justice':
      return <JusticeSection vm={vm} />;
    case 'gate':
      return <GateSection vm={vm} />;
    case 'inequality':
      return <InequalitySection vm={vm} perYear={perYear} />;
    case 'knowledge':
      return <KnowledgeSection vm={vm} perYear={perYear} jobLabel={jobLabel} />;
    case 'fragility':
      return <FragilitySection vm={vm} perYear={perYear} />;
    case 'sketch':
      return <SketchSection sketch={vm.sketch} />;
  }
}

export function LedgerView(props: LedgerViewProps) {
  const { vm, section, onSection, copied } = props;
  const o = vm.outcome;
  const tabId = (s: LedgerSectionId): string => `ledger-tab-${s}`;
  return (
    <article class="ledger" aria-labelledby="ledger-title">
      <header class={`ledger__banner ledger__banner--${o.kind}`}>
        <h2 id="ledger-title">{t.title}</h2>
        <p class="ledger__outcome">{outcomeLine(vm)}</p>
        <p class="ledger__seed">
          {t.seed}: <span class="num">{o.seed}</span>{' '}
          <button type="button" class="button button--small" onClick={props.onCopySeed}>
            {t.copySeed}
          </button>
          {copied === 'seed' && (
            <span class="hint" role="status">
              {' '}
              {t.copied}
            </span>
          )}
        </p>
        <p class="hint">
          {ledgerOptionsLine(o.options)}
          {o.ironman ? ` · ${t.ironman}` : ''}
          {o.sandbox ? ` · ${t.sandbox}` : ''} · {t.steadyCount(o.counter, o.target)}
        </p>
        <div class="ledger__tags" aria-label={t.tags}>
          {vm.tags.length === 0 ? (
            <span class="hint">{t.noTags}</span>
          ) : (
            <ul>
              {vm.tags.map((tag) => (
                <li key={tag} class="ledger__tag" title={tagMeaning[tag]}>
                  {tagLabel[tag]}
                </li>
              ))}
            </ul>
          )}
        </div>
        <p class="ledger__score">
          <strong>{t.score}.</strong> {t.scoreLine(vm.score)}
        </p>
      </header>

      <nav class="ledger__tabs" role="tablist" aria-label={t.sections}>
        {LEDGER_SECTIONS.map((s, i) => (
          <button
            key={s}
            id={tabId(s)}
            type="button"
            role="tab"
            class={`ledger__tab${s === section ? ' is-active' : ''}`}
            aria-selected={s === section}
            aria-controls="ledger-panel"
            tabIndex={s === section ? 0 : -1}
            onClick={() => onSection(s)}
            onKeyDown={(e) => {
              const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
              if (step === 0) return;
              e.preventDefault();
              const n = LEDGER_SECTIONS.length;
              const next = LEDGER_SECTIONS[(i + step + n) % n] ?? s;
              onSection(next);
              document.getElementById(tabId(next))?.focus();
            }}
          >
            {i + 1}. {ledgerSectionLabel[s]}
          </button>
        ))}
      </nav>
      <section
        id="ledger-panel"
        class="ledger__panel"
        role="tabpanel"
        aria-labelledby={tabId(section)}
        tabIndex={0}
      >
        <h3>{ledgerSectionLabel[section]}</h3>
        <Section vm={vm} section={section} jobLabel={props.jobLabel} />
      </section>

      <footer class="ledger__actions">
        {o.canContinueSandbox && props.onContinueSandbox !== undefined && (
          <button type="button" class="button" onClick={props.onContinueSandbox}>
            {t.continueSandbox}
          </button>
        )}
        {props.onExport !== undefined && (
          <button type="button" class="button" onClick={props.onExport}>
            {t.exportRun}
          </button>
        )}
        <button type="button" class="button" onClick={props.onCopySummary}>
          {t.copySummary}
        </button>
        {copied === 'summary' && (
          <span class="hint" role="status">
            {t.copied}
          </span>
        )}
        <button type="button" class="button button--primary" data-autofocus onClick={props.onBack}>
          {t.back}
        </button>
      </footer>
    </article>
  );
}
